import React from "react";
import type { LabelProps } from "@fluentui/react-label";
import type {
  DataGridCellFocusMode,
  TableColumnDefinition,
  TableColumnId,
  TableColumnSizingOptions,
} from "@fluentui/react-table";
import type { TFunction } from "i18next";
import { Button, ButtonProps } from "@fluentui/react-button";
import {
  ArrowRepeatAllOff16Regular,
  ArrowSync16Regular,
  CheckmarkLock20Regular,
  LockMultipleRegular,
  Open16Regular,
} from "@fluentui/react-icons";
import { Label } from "@fluentui/react-label";
import {
  DataGridBody,
  DataGridRow,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
} from "@fluentui/react-table";
import { tokens } from "@fluentui/react-theme";
import { Tooltip } from "@fluentui/react-tooltip";
import { useId } from "@fluentui/react-utilities";
import { makeStyles } from "@griffel/react";
import { useTranslation } from "react-i18next";
import { Room } from "../domain/Room";
import { selectRoom, showRoomInfo } from "../../service/ActionHandler";

const buttonProps: ButtonProps = {
  size: "small",
  appearance: "transparent",
};

function buildColumns(t: TFunction<"strings", undefined>): TableColumnDefinition<Room>[] {
  return [
    createTableColumn<Room>({
      columnId: "fl_id",
      compare: (a, b) => {
        return a.fl_id.localeCompare(b.fl_id);
      },
      renderHeaderCell: () => {
        return t("fl_id_header");
      },
      renderCell: (item) => {
        return <TableCellLayout>{item.fl_id}</TableCellLayout>;
      },
    }),
    createTableColumn<Room>({
      columnId: "rm_id",
      compare: (a, b) => {
        return a.rm_id.localeCompare(b.rm_id);
      },
      renderHeaderCell: () => {
        return t("id");
      },
      renderCell: (item) => {
        return <TableCellLayout>{item.rm_id}</TableCellLayout>;
      },
    }),
    createTableColumn<Room>({
      columnId: "name",
      compare: (a, b) => {
        return a.name.localeCompare(b.name);
      },
      renderHeaderCell: () => {
        return t("name");
      },
      renderCell: (item) => {
        return <TableCellLayout>{item.name}</TableCellLayout>;
      },
    }),
    createTableColumn<Room>({
      columnId: "min_required",
      compare: (a, b) => {
        return a.min_required - b.min_required;
      },
      renderHeaderCell: () => {
        return t("min");
      },
      renderCell: (item) => {
        return <TableCellLayout>{item.min_required}</TableCellLayout>;
      },
    }),
    createTableColumn<Room>({
      columnId: "max_capacity",
      compare: (a, b) => {
        return a.max_capacity - b.max_capacity;
      },
      renderHeaderCell: () => {
        return t("max");
      },
      renderCell: (item) => {
        return <TableCellLayout>{item.max_capacity}</TableCellLayout>;
      },
    }),
    createTableColumn<Room>({
      columnId: "approval",
      compare: (a, b) => {
        return Number(a.approval) - Number(b.approval);
      },
      renderHeaderCell: () => {
        return (
          <Tooltip content={t("restrictions")} relationship="label">
            <Button icon={<LockMultipleRegular />} {...buttonProps} />
          </Tooltip>
        );
      },
      renderCell: (item) => {
        if (item.approval) {
          return (
            <Tooltip content={t("approval_required")} relationship="label">
              <Button icon={<CheckmarkLock20Regular />} style={{ cursor: "default" }} {...buttonProps} />
            </Tooltip>
          );
        } else return;
      },
    }),
    createTableColumn<Room>({
      columnId: "info",
      renderHeaderCell: () => {
        return t("info");
      },
      renderCell: () => {
        return (
          <Tooltip content={t("show_info")} relationship="label">
            <Button icon={<Open16Regular />} {...buttonProps} />
          </Tooltip>
        );
      },
    }),
    createTableColumn<Room>({
      columnId: "num_conflicts",
      compare: (a, b) => {
        return a.num_conflicts - b.num_conflicts;
      },
      renderHeaderCell: () => {
        return (
          <Tooltip content={t("conflicts")} relationship="label">
            <Button icon={<ArrowRepeatAllOff16Regular />} {...buttonProps} />
          </Tooltip>
        );
      },
      renderCell: (item) => {
        return <TableCellLayout>{item.num_conflicts}</TableCellLayout>;
      },
    }),
  ];
}

const getCellFocusMode = (columnId: TableColumnId): DataGridCellFocusMode => {
  switch (columnId) {
    case "info":
      return "none";
    default:
      return "cell";
  }
};

const renderRefreshAction = (searchButtonStyle: string, disabled: boolean, onClick: () => void, labelText: string) => {
  // TODO ensure spinner doesn't cause layout to jump around
  // return <Spinner size="extra-tiny" />;
  return (
    <Tooltip content={labelText} relationship="label">
      <Button
        className={searchButtonStyle}
        icon={<ArrowSync16Regular />}
        onClick={onClick}
        disabled={disabled}
        {...buttonProps}
      />
    </Tooltip>
  );
};

const columnSizingOptions: TableColumnSizingOptions = {
  fl_id: {
    minWidth: 25,
    idealWidth: 35,
  },
  rm_id: {
    minWidth: 20,
    idealWidth: 35,
  },
  name: {
    minWidth: 50,
    idealWidth: 150,
  },
  min_required: {
    minWidth: 19,
    idealWidth: 25,
  },
  max_capacity: {
    minWidth: 19,
    idealWidth: 25,
  },
  approval: {
    minWidth: 23,
    idealWidth: 30,
  },
  info: {
    minWidth: 15,
    idealWidth: 20,
  },
  num_conflicts: {
    minWidth: 23,
    idealWidth: 30,
  },
};

const useStyles = makeStyles({
  roomListStyle: {
    marginTop: tokens.spacingVerticalXL,
    paddingBottom: tokens.spacingVerticalM,
    marginLeft: tokens.spacingHorizontalM,
    marginRight: tokens.spacingHorizontalM,
    "> label": {
      marginBottom: tokens.spacingVerticalS,
      marginLeft: tokens.spacingHorizontalS,
      marginRight: tokens.spacingHorizontalS,
    },
  },
  roomListHeaderStyle: {
    fontSize: "smaller",
  },
  searchButtonStyle: {
    display: "float",
  },
});

const gridLabelProps: LabelProps = {
  size: "small",
};

interface RoomListProps {
  buildingId: string;
  floorId: string;
  arrangeTypeId: string;
  capacity: number | null;
  attributes: string[];
  loading: boolean;
  initialized: boolean;
  rooms: Room[];
  updateRoomList: () => void;
}

const roomCache = new Map<string, Room>();
const otherEmailCache = new Set<string>();

const RoomList = (props: RoomListProps): React.JSX.Element => {
  if (!props.initialized) {
    return <></>;
  }

  const { t } = useTranslation();
  const styles = useStyles();
  const roomGridId = useId();
  const columns = buildColumns(t);

  // filter visible columns - if no rooms don't show any headers
  let visibleColumnNames = "";
  if (props.rooms.length > 0) {
    visibleColumnNames = props.rooms[0].columns;
  }
  const visibleColumns = columns.filter((column) => visibleColumnNames.includes(column.columnId.toString()));

  return (
    <div className={styles.roomListStyle}>
      <Label htmlFor={roomGridId} {...gridLabelProps}>
        {t("available_rooms")}
      </Label>
      {renderRefreshAction(styles.searchButtonStyle, props.loading, props.updateRoomList, t("refresh_room_list"))}
      <DataGrid
        id={roomGridId}
        items={props.rooms}
        columns={visibleColumns}
        sortable
        resizableColumns
        getRowId={(item: Room) => item.getIdentifier()}
        size="small"
        columnSizingOptions={columnSizingOptions}
      >
        <DataGridHeader className={styles.roomListHeaderStyle}>
          <DataGridRow>
            {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<Room>>
          {({ item, rowId }) => (
            <DataGridRow<Room> key={rowId}>
              {({ renderCell, columnId }) => (
                <DataGridCell
                  focusMode={getCellFocusMode(columnId)}
                  onClick={() => {
                    if (columnId == "info") {
                      showRoomInfo(item);
                    } else {
                      selectRoom(item, roomCache, otherEmailCache);
                    }
                  }}
                >
                  {renderCell(item)}
                </DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </div>
  );
};

export default RoomList;
